import { ArrowUpRight, Code } from 'lucide-react';
import { useState, useCallback, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { usePreview } from '@/Domain/components/PreviewProvider';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/Domain/components/Shared/Dialog/Dialog';
import { AutosizeTextarea } from '@/Domain/components/Shared/Textarea/Textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { useCollapsibleLabel } from '@/Domain/hooks/App/useCollapsibleLabel.hook';

const DOCS_URL = 'https://docs.quickchat.ai/channels/website/#advanced-features-custom-parameters';

const DocsLink = ({ children }: { children?: React.ReactNode }) => (
  <a
    href={DOCS_URL}
    target="_blank"
    rel="noopener noreferrer"
    className="text-foreground font-medium hover:underline inline-flex items-center gap-0.5"
  >
    {children}
    <ArrowUpRight className="h-3.5 w-3.5" />
  </a>
);

export const AdvancedSettings = () => {
  const { t } = useTranslation('ai_preview_page');
  const { metadata, setMetadata } = usePreview();
  const [open, setOpen] = useState(false);
  const [jsonError, setJsonError] = useState(false);
  const [localMetadata, setLocalMetadata] = useState(metadata);

  useEffect(() => {
    setLocalMetadata(metadata);
  }, [metadata]);

  const formatJson = useCallback((value: string): string => {
    try {
      const parsed = JSON.parse(value);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return value;
    }
  }, []);

  const validateJson = useCallback((value: string): boolean => {
    if (!value.trim()) return true;
    try {
      JSON.parse(value);
      return true;
    } catch {
      return false;
    }
  }, []);

  const handleMetadataChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      setLocalMetadata(value);
      const isValid = validateJson(value);
      setJsonError(!isValid);
      if (isValid) {
        setMetadata(value);
      }
    },
    [validateJson, setMetadata],
  );

  const handleMetadataBlur = useCallback(() => {
    if (!jsonError && localMetadata.trim()) {
      const formatted = formatJson(localMetadata);
      setLocalMetadata(formatted);
      setMetadata(formatted);
    }
  }, [localMetadata, jsonError, formatJson, setMetadata]);

  const triggerLabel = t('advanced_settings.trigger_label');

  // Label collapses at the @2xl container query; tooltip appears once it's gone (like the preview tabs).
  const {
    observeRef: buttonRef,
    sentinelRef: labelRef,
    isHidden: labelHidden,
  } = useCollapsibleLabel<HTMLButtonElement, HTMLSpanElement>();

  return (
    <>
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              ref={buttonRef}
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 shrink-0 @max-2xl:gap-0 hover:!text-foreground"
              onClick={() => setOpen(true)}
              aria-label={triggerLabel}
              data-testid="advanced-settings-trigger"
            >
              <Code />
              <span ref={labelRef} className="hidden @2xl:inline">
                {t('advanced_settings.title')}
              </span>
            </Button>
          </TooltipTrigger>
          {labelHidden && (
            <TooltipContent side="bottom">{t('advanced_settings.title')}</TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="advanced-settings-dialog">
          <DialogHeader>
            <DialogTitle>
              <Code className="w-5 h-5" />
              {t('advanced_settings.title')}
            </DialogTitle>
            <DialogDescription>
              <Trans
                i18nKey="ai_preview_page:advanced_settings.conversation_metadata.description"
                components={{ docs_link: <DocsLink /> }}
              />
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-3">
            <div className="relative">
              <AutosizeTextarea
                value={localMetadata}
                onChange={handleMetadataChange}
                onBlur={handleMetadataBlur}
                placeholder={t('advanced_settings.conversation_metadata.placeholder')}
                error={jsonError}
                className="font-mono text-xs min-h-[300px] px-4 bg-card border-border"
              />
              {jsonError && (
                <p className="text-destructive text-xs mt-1.5">
                  {t('advanced_settings.conversation_metadata.invalid_json')}
                </p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
